/**
 * MIDI device library API client
 *
 * Talks to the Rails backend's public /api/midi_devices endpoints (search +
 * detail) so the editor can offer "load a preset from the MIDI library".
 * GET-only — no CSRF token needed (Rails only enforces forgery protection
 * on unsafe HTTP verbs).
 *
 * Shared between web-editor (always has a route to this API) and
 * midulate-editor (only usable when the device is in station mode, with
 * real internet access — see DeviceEditModal's wifiStatus gating).
 */
export class MidiLibraryApiClient {
  constructor(baseUrl = '/api/midi_devices') {
    this.baseUrl = baseUrl;
  }

  /**
   * Search approved devices by manufacturer or name.
   * @param {string} query
   * @returns {Promise<Array<{id: string, manufacturer: string, name: string, openmidi_slug: string}>>}
   */
  async search(query) {
    try {
      const url = query ? `${this.baseUrl}?q=${encodeURIComponent(query)}` : this.baseUrl;
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Search failed: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to search MIDI device library:', error);
      throw error;
    }
  }

  /**
   * Fetch full detail (including cc_mappings) for a single device.
   * @param {string} id
   * @returns {Promise<{id: string, manufacturer: string, name: string, openmidi_slug: string, manual_url: string|null, cc_mappings: Array<{cc_number: number, label: string}>}>}
   */
  async getDevice(id) {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Fetching device failed: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('Failed to fetch MIDI device detail:', error);
      throw error;
    }
  }
}

export default MidiLibraryApiClient;
